import { Icon, type IconName } from './icon/index.ts';
import styles from './Button.module.css';

/**
 * Design system § 14 — the icon variant: 52px, L2, meeting the minimum target
 * without a visible label.
 *
 * `label` is required, not optional. An icon-only control with no accessible
 * name is unusable by a screen-reader user, and § 12 admits no exception.
 */

interface IconButtonProps {
  icon: IconName;
  /** The accessible name. Required — there is no visible text to fall back on. */
  label: string;
  onClick?: () => void;
  /**
   * § 6 — pure circles belong to the domain of sound and of people. Anything
   * else takes the square (radius-sm) shape.
   */
  shape?: 'circle' | 'square';
  disabled?: boolean;
  /** Required when disabled, for the same reason as on Button (§ 12). */
  disabledReason?: string;
  ariaKeyShortcuts?: string;
  /**
   * § 16 — `compact` is the administrative height, for a control inside a
   * table row or a toolbar. --control-height-compact is --target-min at
   * touch, so the § 12 floor holds at every viewport.
   */
  size?: 'default' | 'compact';
}

export function IconButton({
  icon,
  label,
  onClick,
  shape = 'circle',
  disabled = false,
  disabledReason,
  ariaKeyShortcuts,
  size = 'default',
}: IconButtonProps) {
  return (
    <button
      type="button"
      className={styles.iconButton}
      data-shape={shape}
      data-size={size}
      data-state={disabled ? 'disabled' : 'default'}
      disabled={disabled}
      onClick={onClick}
      aria-label={disabled && disabledReason ? `${label} — ${disabledReason}` : label}
      aria-keyshortcuts={ariaKeyShortcuts}
    >
      <Icon name={icon} size={size === 'compact' ? 20 : 24} />
    </button>
  );
}
